import { useData } from "../../runtime/provider";
import { usePlayerDiagnostics } from "@components/SwipeList/Slot.diagnostics";
import { usePlayerActions } from "@utils/player/core/usePlayer";
import LBox from "@components/LBox";
import Slot from "@components/SwipeList/Slot";

import { SwipeListComponentProps } from "@components/SwipeList/types";
import {
  useForyouSwipeEngine,
  usePlaylistSwipeEngine,
} from "@hooks/useSharedSwipe";

import {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import {
  useSharedValue,
  withSpring,
  useAnimatedStyle,
  SharedValue,
} from "react-native-reanimated";
import { router } from "expo-router";
import { useHasHydrated, usePlayer } from "@utils/player/core/usePlayer";
import {
  selectCanSkipState,
  selectIsAutoPlayEnabled,
} from "@utils/player/core/selectors";
import { QueueItem } from "@utils/player/utils/queue.types";
import { useForyouQueue, usePlaylistQueue } from "@utils/player";
import { scheduleOnRN } from "react-native-worklets";

const ANIMATION_CONFIG = {
  damping: 50,
  stiffness: 400,
  mass: 0.3,
  overshootClamping: true,
};

interface SwipeListProps {
  Component: ComponentType<SwipeListComponentProps>;
  disabled?: boolean;
  height: number;
  queueType: "foryou" | "playlist";
  surface?: "feed" | "bar" | "unknown";
}

const SwipeListBase = ({
  Component,
  disabled,
  height,
  engine,
  queueType,
  surface = "unknown",
  activeIndex,
  items,
  playPauseSongAtIndex,
}: {
  Component: ComponentType<SwipeListComponentProps>;
  disabled?: boolean;
  height: number;
  engine: {
    baseHeight: number;
    numberOfSlots: number;
    progress: SharedValue<number>;
    targetOffset: SharedValue<number>;
  };
  queueType: "foryou" | "playlist";
  surface?: "feed" | "bar" | "unknown";
  activeIndex: number | undefined;
  items: QueueItem[] | undefined;
  playPauseSongAtIndex: (index: number) => void;
}) => {
  const session = useData();
  const { captureBlankSwipeListState } = usePlayerDiagnostics();

  const { baseHeight, numberOfSlots, progress, targetOffset } = engine;
  const initialOffset = (activeIndex ?? 0) * baseHeight;

  const offset = useSharedValue(initialOffset);
  const gestureStart = useSharedValue(0);
  const gestureStartIndex = useSharedValue(activeIndex ?? 0);
  const isGestureActive = useSharedValue(false);

  // PERF: Single subscription for canSkip state
  const canSkipState = usePlayer(selectCanSkipState);
  const activeQueueType = usePlayer((state) => state.activeQueueType);
  const canSkip = useSharedValue(canSkipState);
  const prevCanSkipRef = useRef(canSkipState);

  useEffect(() => {
    // Only update shared value if the values actually changed
    if (
      prevCanSkipRef.current.canSkip !== canSkipState.canSkip ||
      prevCanSkipRef.current.reason !== canSkipState.reason
    ) {
      prevCanSkipRef.current = canSkipState;
      canSkip.set(canSkipState);
    }
  }, [canSkipState, canSkip]);

  useEffect(() => {
    // Prefetch active artist's access to reduce initial load delay
    const prefetchIndex = activeIndex ?? 0;
    const item =
      items && items.length > 0 && prefetchIndex < items.length
        ? items[prefetchIndex]
        : undefined;
    const artistId = item?.metadata.artists.at(0)?.id;

    if (artistId) {
      void session.utils.accesses.getFeedForArtistId
        .prefetch({ artistId })
        .catch((error) => {
          console.error("Failed to prefetch artist access:", error);
        });
    }
  }, [activeIndex, items]);

  // Track previous activeIndex to prevent unnecessary updates.
  // Held as render state (not a ref) so it can be read/written during render
  // without tripping the refs rule; React explicitly supports adjusting state
  // during render via the "store previous value" pattern below.
  const [prevActiveIndex, setPrevActiveIndex] = useState<number | undefined>(
    undefined,
  );
  // Whether the first activeIndex transition has been applied. Held as render state
  // (not a ref) so it can be read/written during render without tripping the refs rule.
  const [hasInitialized, setHasInitialized] = useState(false);
  // Track items reference to detect queue switches (render state, see above).
  const [prevItems, setPrevItems] = useState<QueueItem[] | undefined>(
    undefined,
  );

  // CRITICAL: Detect queue switch / activeIndex change and update progress
  // SYNCHRONOUSLY during render. This must happen BEFORE effects run, because Slot's
  // useEffect([items]) reads progress to determine which item to display. If we deferred
  // this to our own useEffect, Slot's effect would run first and read stale progress.
  //
  // All writes here target reanimated shared values (allowed during render) plus the
  // store-previous-value-during-render pattern (React-supported, no extra commit) for the
  // prevItems / prevActiveIndex trackers — replacing the refs that previously tripped the
  // refs rule.
  const isQueueSwitch =
    items !== prevItems && prevItems !== undefined && items !== undefined;
  if (items !== prevItems) {
    setPrevItems(items);
  }

  if (isQueueSwitch && activeIndex !== undefined && !isGestureActive.get()) {
    // Queue switch: set all values immediately - no animation.
    const targetValue = activeIndex * baseHeight;
    targetOffset.set(targetValue);
    progress.set(targetValue);
    offset.set(targetValue);
    if (activeIndex !== prevActiveIndex) setPrevActiveIndex(activeIndex);
  } else if (activeIndex === undefined) {
    if (prevActiveIndex !== undefined) setPrevActiveIndex(undefined);
  } else if (activeIndex !== prevActiveIndex && !isGestureActive.get()) {
    // activeIndex changed (not a queue switch) and no gesture in flight: animate.
    // While a gesture is active we intentionally leave prevActiveIndex unchanged so
    // this transition is applied on a later render once the gesture releases.
    const targetValue = activeIndex * baseHeight;
    if (!hasInitialized) {
      setHasInitialized(true);
      targetOffset.set(targetValue);
      progress.set(targetValue);
      offset.set(targetValue);
    } else if (Math.abs(targetOffset.get() - targetValue) > 1) {
      targetOffset.set(targetValue);
      offset.set(withSpring(targetValue, ANIMATION_CONFIG)); // Update offset first
      progress.set(withSpring(targetValue, ANIMATION_CONFIG)); // Then update progress
    }
    setPrevActiveIndex(activeIndex);
  }

  const itemsLength = items?.length ?? 0;
  const maxOffset = Math.max(0, (itemsLength - 1) * baseHeight);

  useEffect(() => {
    if (activeQueueType !== queueType) return;

    const timeout = setTimeout(() => {
      const itemCount = items?.length ?? 0;
      const progressValue = progress.get();
      const targetOffsetValue = targetOffset.get();
      const progressIndex =
        baseHeight > 0
          ? Math.floor(Math.max(0, progressValue) / baseHeight)
          : null;
      const targetIndex =
        baseHeight > 0
          ? Math.floor(Math.max(0, targetOffsetValue) / baseHeight)
          : null;

      if (itemCount === 0) {
        captureBlankSwipeListState({
          activeIndex,
          baseHeight,
          itemCount,
          progressIndex,
          progressValue,
          queueType,
          reason: "active-list-empty",
          surface,
          targetIndex,
          targetOffsetValue,
        });
        return;
      }

      if (
        activeIndex === undefined ||
        activeIndex < 0 ||
        activeIndex >= itemCount
      ) {
        captureBlankSwipeListState({
          activeIndex,
          baseHeight,
          itemCount,
          progressIndex,
          progressValue,
          queueType,
          reason: "active-index-invalid",
          surface,
          targetIndex,
          targetOffsetValue,
        });
        return;
      }

      if (progressIndex !== null && progressIndex >= itemCount) {
        captureBlankSwipeListState({
          activeIndex,
          baseHeight,
          itemCount,
          progressIndex,
          progressValue,
          queueType,
          reason: "progress-index-invalid",
          surface,
          targetIndex,
          targetOffsetValue,
        });
      }
    }, 2_000);

    return () => clearTimeout(timeout);
  }, [
    activeIndex,
    activeQueueType,
    baseHeight,
    items,
    progress,
    queueType,
    surface,
    targetOffset,
  ]);

  const panGesture = useMemo(
    () =>
      Gesture.Pan()
        // .enabled(enableSwipe)
        .onStart(() => {
          isGestureActive.set(true);
          // When a new gesture starts mid-animation, snap offset to targetOffset
          // This ensures onUpdate calculates from the correct position
          offset.set(targetOffset.value);
          // DON'T snap progress.value - keep the current visual position for smooth transitions
          // Use current progress.value as the starting point for dragDistance calculation
          gestureStart.set(progress.value);
          // Calculate index from targetOffset (the actual target) instead of progress (which is animating)
          // This ensures we get the correct index even if animation is mid-flight
          const indexFromTarget = Math.floor(targetOffset.value / baseHeight);
          gestureStartIndex.set(indexFromTarget);
          // No need to call runOnJS - canSkip is already in shared value, read directly
        })
        .onUpdate((e) => {
          if (!canSkip.value.canSkip) {
            const restrictedTranslation = e.translationY * 0.7;
            progress.set(
              Math.min(
                maxOffset,
                Math.max(0, offset.value - restrictedTranslation),
              ),
            );
          } else {
            progress.set(
              Math.min(maxOffset, Math.max(0, offset.value - e.translationY)),
            );
          }
        })
        .onEnd((e) => {
          if (!canSkip.value.canSkip) {
            progress.set(withSpring(gestureStart.value, ANIMATION_CONFIG));
            const infoVariant = canSkip.value.reason === "ad" ? "ad" : "skip";
            scheduleOnRN(router.push, `/paywall?infoVariant=${infoVariant}`);
            return;
          }

          const velocity = e.velocityY;
          const VELOCITY_THRESHOLD = 500;

          const dragDistance = progress.value - gestureStart.value;

          if (dragDistance === 0) {
            // User is swiping down on first song or up on last song. Do nothing
            return;
          }

          // Calculate new index based on activeIndex and swipe direction
          // This ensures we use the current state, not stale offset values
          let newIndex: number;

          if (Math.abs(velocity) > VELOCITY_THRESHOLD) {
            // Quick swipe - go one song in swipe direction
            newIndex =
              velocity > 0
                ? gestureStartIndex.value - 1 // Swipe down = previous song
                : gestureStartIndex.value + 1; // Swipe up = next song
          } else {
            // Slow swipe - determine direction from drag distance
            if (dragDistance > height / 4) {
              // Swipe right = next song
              newIndex = gestureStartIndex.value + 1;
            } else if (dragDistance < -height / 4) {
              // Swipe left = previous song
              newIndex = gestureStartIndex.value - 1;
            } else {
              // Not enough drag, don't change
              newIndex = gestureStartIndex.value;
            }
          }

          // Clamp index to valid range
          newIndex = Math.max(0, Math.min(newIndex, itemsLength - 1));

          // Only proceed if index actually changed
          if (newIndex !== gestureStartIndex.value) {
            const calculatedIndex = newIndex;
            const newTargetOffset = calculatedIndex * baseHeight;

            targetOffset.set(Math.min(Math.max(0, newTargetOffset), maxOffset));

            // Use the calculated index directly instead of recalculating from offset
            const finalIndex = calculatedIndex;

            scheduleOnRN(playPauseSongAtIndex, finalIndex);
            progress.set(
              withSpring(targetOffset.value, ANIMATION_CONFIG, (finished) => {
                if (finished) {
                  offset.set(progress.value);
                  // Don't update targetOffset here - it's already set correctly in onEnd
                  isGestureActive.set(false);
                }
              }),
            );
          } else {
            progress.set(withSpring(gestureStart.value, ANIMATION_CONFIG));
            isGestureActive.set(false);
          }
        }),
    [
      baseHeight,
      height,
      itemsLength,
      maxOffset,
      playPauseSongAtIndex,
      canSkip,
      gestureStart,
      gestureStartIndex,
      isGestureActive,
      offset,
      progress,
      targetOffset,
    ],
  );

  const containerStyle = useAnimatedStyle(() => {
    const translateY = -progress.value * (height / baseHeight);
    return {
      transform: [{ translateY }],
    };
  });

  return (
    items && (
      <GestureDetector gesture={panGesture}>
        <LBox>
          <LBox width="100%" style={containerStyle} height={height}>
            {Array.from({ length: numberOfSlots }, (_, index) => (
              <Slot
                activeIndex={activeIndex}
                Component={Component}
                disabled={disabled}
                index={index}
                items={items}
                key={`slot-${index}`}
                queueType={queueType}
                surface={surface}
                progress={progress}
                isGestureActive={isGestureActive}
                height={height}
                baseHeight={baseHeight}
                numberOfSlots={numberOfSlots}
              />
            ))}
          </LBox>
        </LBox>
      </GestureDetector>
    )
  );
};

const SwipeListForyou = ({
  Component,
  disabled,
  height,
  surface,
}: Omit<SwipeListProps, "queueType">) => {
  const playerActions = usePlayerActions();

  const engine = useForyouSwipeEngine();
  const foryouQueue = useForyouQueue();
  const isAutoPlayEnabled = usePlayer(selectIsAutoPlayEnabled);
  const items = foryouQueue?.items;
  const activeIndex = foryouQueue?.currentIndex;
  const playPauseSongAtIndex = useCallback(
    (index: number) => {
      const item = items && items[index] ? items[index] : undefined;
      if (item) {
        // Keep the existing instrumentation in useSharedSwipe.ts for now;
        // this is the new call site.
        playerActions.playFromQueueItem(item.qid, {
          autoPlay: isAutoPlayEnabled,
        });
      }
    },
    [items, isAutoPlayEnabled],
  );
  return (
    <SwipeListBase
      Component={Component}
      disabled={disabled}
      height={height}
      engine={engine}
      queueType="foryou"
      surface={surface}
      items={items}
      activeIndex={activeIndex}
      playPauseSongAtIndex={playPauseSongAtIndex}
    />
  );
};

const SwipeListPlaylist = ({
  Component,
  disabled,
  height,
  surface,
}: Omit<SwipeListProps, "queueType">) => {
  const playerActions = usePlayerActions();

  const engine = usePlaylistSwipeEngine();
  const playlistQueue = usePlaylistQueue();
  const isAutoPlayEnabled = usePlayer(selectIsAutoPlayEnabled);
  const items = playlistQueue?.items;
  const activeIndex = playlistQueue?.currentIndex;
  const playPauseSongAtIndex = useCallback(
    (index: number) => {
      const item = items && items[index] ? items[index] : undefined;
      if (item) {
        playerActions.playFromQueueItem(item.qid, {
          autoPlay: isAutoPlayEnabled,
        });
      }
    },
    [items, isAutoPlayEnabled],
  );
  return (
    <SwipeListBase
      Component={Component}
      disabled={disabled}
      height={height}
      engine={engine}
      queueType="playlist"
      surface={surface}
      items={items}
      activeIndex={activeIndex}
      playPauseSongAtIndex={playPauseSongAtIndex}
    />
  );
};

const SwipeList = ({
  Component,
  disabled,
  height,
  queueType,
  surface,
}: SwipeListProps) => {
  const { capturePlayerBlankRenderGate } = usePlayerDiagnostics();

  const hasHydrated = useHasHydrated();
  useEffect(() => {
    if (hasHydrated) return;

    const timeout = setTimeout(() => {
      capturePlayerBlankRenderGate({
        extra: {
          height,
          queueType,
        },
        queueType,
        reason: "swipe-list-not-hydrated",
        surface: "swipe-list",
      });
    }, 3_000);

    return () => clearTimeout(timeout);
  }, [hasHydrated, height, queueType]);

  if (!hasHydrated) return null;
  if (queueType === "foryou")
    return (
      <SwipeListForyou
        Component={Component}
        disabled={disabled}
        height={height}
        surface={surface}
      />
    );
  if (queueType === "playlist")
    return (
      <SwipeListPlaylist
        Component={Component}
        disabled={disabled}
        height={height}
        surface={surface}
      />
    );
  return null;
};

export default SwipeList;
